<template>
	<main class="org-settings-referral">
		<CSettingsSection
			:loading="loading"
			:loading-size="3"
		>
			<div>
				<h3 class="font-display sz-4 mb-4">Spread the word and earn 20%</h3>
				<p class="mb-4">Recommend Cognito Forms to your friends and colleagues, and they'll receive a free month of advanced form building. When they sign up, you'll receive 20% of their monthly subscription for the first 12 months. <a href="#" class="text-link underline" @click="openDetailsDialog">See details.</a></p>
				<label for="ReferralUrl" class="block heavy mb-2">Get your referral link and share it.</label>
				<div class="referral-container relative flex mb-2">
					<input id="ReferralUrl" readonly type="text" :value="referralSettings.ReferralUrl" @click="copyReferralUrl" />
					<a target="_blank" title="Open Referral Link" :href="referralSettings.ReferralUrl" class="absolute right-0 p-2"><Inline svg="external-link" /></a>
				</div>
				<div ref="referralUrlCopied" class="text-helptext opacity-0">Link copied to clipboard...</div>
				<div v-if="referralSettings.CanManageReferralProgram">
					<label for="rdoIncludeMarketMsg" class="block heavy mt-4">
						Include referral link on your forms?
						<CIconButton
							icon="support"
							:intense="true"
							class="inline-flex align-middle"
							@click="openBrandingDialog"
						/>
					</label>
					<CSwitch
						id="rdoIncludeMarketMsg"
						v-model="internalIncludeBranding"
						@input="$emit('update-referral-info', $event)"
					/>
				</div>
			</div>
			<div v-if="referralSettings.Referrals?.length > 0">
				<h3 class="sz-4 mb-3 mt-5">My Referrals</h3>
				<table class="c-referral table table-style">
					<tr class="referral-header"><th>Organization</th><th>Date</th><th>Plan</th><th>Credits Earned</th></tr>
					<tr v-for="(ref, index) in referralSettings.Referrals" :key="index">
						<td>{{ ref.OrganizationName }}</td>
						<td>{{ formatDate(ref.SignUpDate) }}</td>
						<td>{{ ref.PlanName + (ref.TrialEndDate !== null ? " (Trial)" : "") }}</td>
						<td>{{ ref.CreditsEarned > 0 ? ref.CreditsEarned : "-" }}</td>
					</tr>
				</table>
			</div>
		</CSettingsSection>
		<CReferralBrandingDialog
			:show-dialog="showBrandingDialog"
			@close="handleCloseBrandingDialog"
		>
		</CReferralBrandingDialog>
		<CReferralProgramDetailsDialog
			:show-dialog="showDetailsDialog"
			@close="handleCloseDetailsDialog"
		>
		</CReferralProgramDetailsDialog>
	</main>
</template>

<script lang="ts">
	import { defineComponent, onMounted, ref, watch } from 'vue';
	import CReferralBrandingDialog from './ReferralBrandingDialog.vue';
	import CReferralProgramDetailsDialog from './ReferralProgramDetailsDialog.vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CSwitch from 'src/components/form-controls/Switch.vue';
	import CSettingsSection from './SettingsSection.vue';

	export default defineComponent({
		components: {
			CReferralBrandingDialog,
			CReferralProgramDetailsDialog,
			CIconButton,
			CSwitch,
			CSettingsSection
		},
		props: {
			referralSettings: {
				type: Object, required: true
			},
			loading: { type: Boolean, default: true }
		},
		setup(props, { emit }) {
			onMounted(() => emit('component-loaded'));
			const referralUrlCopied = ref<HTMLElement | null>(null);
			const copyReferralTimeout = ref<number | null>(null);
			const showBrandingDialog = ref(false);
			const showDetailsDialog = ref(false);
			const internalIncludeBranding = ref(props.referralSettings.IncludeBranding);

			const copyReferralUrl = function () {
				navigator.clipboard.writeText(props.referralSettings.ReferralUrl).then(() => {
					referralUrlCopied.value.style.opacity = '1';
					clearTimeout(copyReferralTimeout.value);

					copyReferralTimeout.value = window.setTimeout(() => {
						referralUrlCopied.value.style.opacity = '0';
					}, 1800);
				}).catch((e) => { console.log(e); });
			};

			const formatDate = function (date: string | Date) {
				// in the case the date is string - convert it to a date object for formatting
				const strictDate = new Date(date);
				return new Intl.DateTimeFormat('en-US').format(strictDate);
			};

			const openBrandingDialog = function() {
				showBrandingDialog.value = true;
			};

			const handleCloseBrandingDialog = function () {
				showBrandingDialog.value = false;
			};

			const handleCloseDetailsDialog = function () {
				showDetailsDialog.value = false;
			};

			const openDetailsDialog = function () {
				showDetailsDialog.value = true;
			};

			watch(
				() => props.referralSettings.IncludeBranding,
				(newVal) => {
					internalIncludeBranding.value = newVal;
				}
			);

			return {
				copyReferralUrl,
				formatDate,
				referralUrlCopied,
				openBrandingDialog,
				handleCloseBrandingDialog,
				showBrandingDialog,
				handleCloseDetailsDialog,
				openDetailsDialog,
				showDetailsDialog,
				internalIncludeBranding
			};
		},
		head() {
			return {
				title: 'Referrals - Cognito Forms',
				titleTemplate: '%s'
			};
		}
	});

</script>

<style lang="scss">
.org-settings-referral {

	.referral-header th {
		position: sticky;
		top: -1px;
		z-index: $z1;
		background: var(--bg-primary);
	}

	.text-helptext {
		transition: opacity .3s ease-in-out;
	}

	.referral-container {

		input {
			padding-right: 28px;
		}
	}

	.support-icon {

		.i-simple {
			vertical-align: text-bottom;
		}
	}

	.settings__loader-content {
		display: block;

		div {
			width: unset;
			height: 75px;
			margin-bottom: $space__4;
		}
	}
}

</style>